import React, {useEffect} from "react";
import {
    AuthProvider,
    authScopeForRoute,
    homeRouteForRole,
    isRouteAllowedForRole,
    resolveAppRole,
    routePath,
    useAuth,
} from "./auth";
import {navigate, useRoute} from "./hooks";
import {AdminChatsPage} from "./pages/admin/AdminChatsPage";
import {AdminLayout, AppLayout, MerchantLayout} from "./components";
import {
    AdminLandingPage,
    AdminActivityPage,
    AdminDisputesPage,
    DepositCasesPage,
    AdminIPAllowlistPage,
    AdminMerchantsPage,
    AdminOrdersPage,
    AdminMerchantWithdrawalsPage,
    AdminTelegramRecipientsPage,
    AdminTradersPage,
    FinancePage,
    StatisticsPage,
    BalancePage,
    HistoryPage,
    LoginPage,
    MerchantBalancePage,
    MerchantCreateOrderPage,
    MerchantNotificationsPage,
    MerchantSettingsPage,
    MerchantOrderDetailsPage,
    MerchantOrdersPage,
    MerchantOverviewPage,
    PaymentsPage,
    SettingsPage,
    TraderNotificationsPage,
} from "./pages";

interface AppRouterProps {
    route: string;
}

function AppRouter({route}: AppRouterProps): React.JSX.Element {
    const {user, logout} = useAuth();
    const role = resolveAppRole(user?.roles);
    const allowedRoute = role ? isRouteAllowedForRole(role, route) : false;
    const path = routePath(route);

    useEffect(() => {
        if (!user || role !== "admin" || !path.startsWith("#/admin/deposit-cases")) return;
        const caseId = new URLSearchParams(route.split("?")[1] ?? "").get("caseId");
        navigate(caseId ? `#/admin/disputes?caseId=${encodeURIComponent(caseId)}` : "#/admin/disputes");
    }, [path, role, route, user]);

    // Hash-router is a UX boundary only; backend RBAC remains authoritative.
    // While the redirect is pending no foreign role layout is rendered.
    useEffect(() => {
        if (!user) return;
        if (!role) {
            logout();
            return;
        }
        if (!allowedRoute) {
            navigate(homeRouteForRole(role));
        }
    }, [allowedRoute, logout, role, user]);

    if (!user) return <LoginPage/>;
    if (!role || !allowedRoute) return <></>;

    if (role === "admin") {
        let page: React.JSX.Element;
        if (path.startsWith("#/admin/merchants")) {
            page = <AdminMerchantsPage/>;
        } else if (path.startsWith("#/admin/merchant-withdrawals")) {
            page = <AdminMerchantWithdrawalsPage/>;
        } else if (path.startsWith("#/admin/ip-allowlist")) {
            page = <AdminIPAllowlistPage/>;
        } else if (path.startsWith("#/admin/telegram")) {
            page = <AdminTelegramRecipientsPage/>;
        } else if (path.startsWith("#/admin/chats")) {
            page = <AdminChatsPage/>;
        } else if (path.startsWith("#/admin/orders")) {
            page = <AdminOrdersPage/>;
        } else if (path.startsWith("#/admin/traders")) {
            page = <AdminTradersPage/>;
        } else if (path.startsWith("#/admin/disputes")) {
            page = <AdminDisputesPage/>;
        } else if (path.startsWith("#/admin/deposit-cases")) {
            page = <AdminDisputesPage/>;
        } else if (path.startsWith("#/admin/history")) {
            page = <HistoryPage/>;
        } else if (path.startsWith("#/admin/activity")) {
            page = <AdminActivityPage/>;
        } else if (path.startsWith("#/admin/finance")) {
            page = <FinancePage/>;
        } else if (path.startsWith("#/admin/statistics")) {
            page = <StatisticsPage/>;
        } else {
            // #/admin, корень, любой другой путь — лендинг админки.
            page = <AdminLandingPage/>;
        }
        return <AdminLayout>{page}</AdminLayout>;
    }

    if (role === "merchant") {
        const detailsMatch = /^#\/merchant\/orders\/([^/]+)$/.exec(path);
        let page: React.JSX.Element;
        // Форма новой заявки — раньше деталей: detailsMatch съел бы "new"
        // как идентификатор заявки.
        if (path === "#/merchant/orders/new") {
            page = <MerchantCreateOrderPage/>;
        } else if (detailsMatch) {
            page = <MerchantOrderDetailsPage orderId={detailsMatch[1]}/>;
        } else if (path === "#/merchant/orders") {
            page = <MerchantOrdersPage/>;
        } else if (path === "#/merchant/balance") {
            page = <MerchantBalancePage/>;
        } else if (path === "#/merchant/withdrawals") {
            page = <MerchantBalancePage/>;
        } else if (path === "#/merchant/settings" || path === "#/merchant/settings/notifications") {
            page = <MerchantSettingsPage/>;
        } else if (path === "#/merchant/notifications") {
            page = <MerchantNotificationsPage/>;
        } else {
            page = <MerchantOverviewPage/>;
        }
        return <MerchantLayout>{page}</MerchantLayout>;
    }

    // Existing trader routes remain unchanged.
    let page: React.JSX.Element;
    if (path === "#/history") {
        page = <HistoryPage/>;
    } else if (path === "#/deposit-cases") {
        page = <DepositCasesPage/>;
    } else if (path === "#/balance") {
        page = <BalancePage/>;
    } else if (path === "#/settings" || path === "#/settings/notifications") {
        page = <SettingsPage/>;
    } else if (path === "#/notifications") {
        page = <TraderNotificationsPage/>;
    } else {
        page = <PaymentsPage/>;
    }
    return <AppLayout>{page}</AppLayout>;
}

export default function App(): React.JSX.Element {
    const route = useRoute();
    const authScope = authScopeForRoute(route);

    return (
        <AuthProvider key={authScope} scope={authScope}>
            <AppRouter route={route}/>
        </AuthProvider>
    );
}
